import { useMutation } from '@redwoodjs/web';
import { createContext, useContext } from 'react';

import type {
  CreateLabelMutation,
  CreateLabelMutationVariables,
  UpdateLabelMutation,
  UpdateLabelMutationVariables,
  SetChangeOrderLabelsMutation,
  SetChangeOrderLabelsMutationVariables,
} from 'types/graphql'

// GraphQL mutation for creating a new label
const CREATE_LABEL_MUTATION = gql`
  mutation CreateLabelMutation($input: CreateLabelInput!) {
    createLabel(input: $input) {
      id
      labelSchema
    }
  }
`

// GraphQL mutation for updating a label
const UPDATE_LABEL_MUTATION = gql`
  mutation UpdateLabelMutation($input: UpdateLabelInput!) {
    updateLabel(input: $input) {
      id
      labelSchema
    }
  }
`

// GraphQL mutation for updating change order labels
const SET_CHANGE_ORDER_LABELS_MUTATION = gql`
  mutation SetChangeOrderLabelsMutation($number: Int!, $input: SetChangeOrderLabelsInput!, $type: OrderType) {
    setChangeOrderLabels(number: $number, input: $input, type: $type) {
      _id
      labels
    }
  }
`


export type Label = {
  id: number;
  name: string;
  color: string;
};

export type LabelSchema = Record<string, Label>;

export const LabelSchemaContext = createContext<LabelSchema | undefined>(undefined);

export const useCreateLabelMutation = (cfg: Parameters<typeof useMutation<CreateLabelMutation, CreateLabelMutationVariables>>[1]) => {
  return useMutation<CreateLabelMutation, CreateLabelMutationVariables>(CREATE_LABEL_MUTATION, cfg)
}

export const useUpdateLabelMutation = (cfg: Parameters<typeof useMutation<UpdateLabelMutation, UpdateLabelMutationVariables>>[1]) => {
  return useMutation<UpdateLabelMutation, UpdateLabelMutationVariables>(UPDATE_LABEL_MUTATION, cfg)
}

export const useSetChangeOrderLabelsMutation = (cfg: Parameters<typeof useMutation<SetChangeOrderLabelsMutation, SetChangeOrderLabelsMutationVariables>>[1]) => {
  return useMutation<SetChangeOrderLabelsMutation, SetChangeOrderLabelsMutationVariables>(SET_CHANGE_ORDER_LABELS_MUTATION, cfg)
}

// Hook to access the label schema
export const useLabelSchema = () => {
  const labelSchema = useContext(LabelSchemaContext);
  if (!labelSchema) {
    throw new Error('useLabelSchema called outside of LabelSchemaContext provider');
  }

  return {
    labelSchema,
    resolveLabels: (labelIds: Parameters<typeof resolveLabels>[0]) => resolveLabels(labelIds, labelSchema),
    useCreateLabelMutation,
    useUpdateLabelMutation,
    useSetChangeOrderLabelsMutation,
  }
};

const resolveLabels = (
  labelIds: number[] = [],
  labelSchema?: LabelSchema
): Label[] => {
  if (!labelSchema || !labelIds.length) return [];

  const filtered = labelIds
    .map((id) => labelSchema[id])
    .filter(Boolean) // Remove any undefined labels (in case a label was deleted)
    .sort((a, b) => a!.name.localeCompare(b!.name)); // Sort alphabetically by name
  return filtered as Label[]
};

/**
 * Get CSS class for a label based on its color
 * @param color The label color string
 * @returns CSS class name for styling the label
 */
export const getLabelColorClass = (color: string): string => {
  const colorMap: Record<string, string> = {
    red: 'bg-red-100 text-red-800',
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    purple: 'bg-purple-100 text-purple-800',
    pink: 'bg-pink-100 text-pink-800',
    indigo: 'bg-indigo-100 text-indigo-800',
    gray: 'bg-gray-100 text-gray-800',
    orange: 'bg-orange-100 text-orange-800',
    teal: 'bg-teal-100 text-teal-800',
  };

  // Default to gray if color is not recognized
  return colorMap[color.toLowerCase()] || 'bg-gray-100 text-gray-800';
};

/**
 * Formats a color string to a standardized format
 * @param color The color string to format
 * @returns Formatted color string
 */
export const formatLabelColor = (color: string): string => {
  // Remove any leading # or whitespace
  return color.replace(/^#|\s+/g, '').toLowerCase();
};

/**
 * Validates a label color
 * @param color The color to validate
 * @returns Boolean indicating if the color is valid
 */
export const isValidLabelColor = (color: string): boolean => {
  // Accept standard color names or hex values
  const validColorNames = [
    'red', 'green', 'blue', 'yellow', 'purple',
    'pink', 'indigo', 'gray', 'orange', 'teal'
  ];

  // Check if it's a valid color name
  if (validColorNames.includes(color.toLowerCase())) {
    return true;
  }

  // Check if it's a valid hex color
  const hexPattern = /^#?([0-9A-F]{3}|[0-9A-F]{6})$/i;
  return hexPattern.test(color);
};

/**
 * Groups labels by color for UI organization
 * @param labels Array of label objects
 * @returns Record with color keys and arrays of labels
 */
export const groupLabelsByColor = (labels: Label[]): Record<string, Label[]> => {
  return labels.reduce((groups, label) => {
    const color = formatLabelColor(label.color);
    if (!groups[color]) {
      groups[color] = [];
    }
    groups[color].push(label);
    return groups;
  }, {} as Record<string, Label[]>);
};

/**
 * Finds a label by name (case-insensitive)
 * @param name The label name to search for
 * @param labelSchema The organization's label schema
 * @returns The found label or undefined
 */
export const findLabelByName = (
  name: string,
  labelSchema?: LabelSchema
): Label | undefined => {
  if (!labelSchema) return undefined;

  const normalizedName = name.toLowerCase();
  return Object.values(labelSchema).find(
    (label) => label.name.toLowerCase() === normalizedName
  );
};

/**
 * Converts a label schema to an array of labels
 * @param labelSchema The organization's label schema
 * @returns Array of label objects
 */
export const labelSchemaToArray = (labelSchema?: LabelSchema): Label[] => {
  if (!labelSchema) return [];
  return Object.values(labelSchema).sort((a, b) => a.name.localeCompare(b.name));
};

